import { TAU, WHITE, circle, glow, rim } from '../paint';
import type { Ctx, Piece } from '../paint';
import { REFERENCE_RADIUS } from '../textures-looks';

export const UNIT = 2;

export const HAIR = { mass: 0x8f4c1c, strands: [0xd98a3a, 0x6e3812, 0xb8682a] } as const;
const HAIR_CAP = '#8f4c1c';
const WOOD = '#c98a4a';
const NECK = '#7a5433';
const HEADSTOCK = '#3a2614';
const HOLE = '#2a1a10';
const LENS = '#7a3a5a';
const RIM = '#d9a441';
const FEATHER = '#f5d33a';
const PETAL = '#f5c518';
const HEART = '#6b3a12';
const OUT = 'rgba(4,6,18,.9)';

const TORSO = { width: 8, height: 12.5 } as const;
const HEAD = { along: 2, radius: 6.8, cap: 0.6, capRadius: 5.8 } as const;
export const HANDS = { strum: [10.5, 6], fret: [14, -14] } as const;
export const STRUM = 1.8;
export const SUNFLOWERS = [
  { along: 0.55, side: -1, size: 0.9 },
  { along: 0.75, side: 1, size: 0.75 },
] as const;

function units(draw: (ctx: Ctx) => void): (ctx: Ctx) => void {
  return (ctx) => {
    ctx.save();
    ctx.scale(UNIT, UNIT);
    draw(ctx);
    ctx.restore();
  };
}

function fillRim(ctx: Ctx, fill: string, width: number): void {
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = width;
  ctx.strokeStyle = OUT;
  ctx.stroke();
}

function line(ctx: Ctx, x0: number, y0: number, x1: number, y1: number): void {
  ctx.beginPath();
  ctx.moveTo(x0, y0);
  ctx.lineTo(x1, y1);
}

function torsoPath(ctx: Ctx): void {
  ctx.beginPath();
  ctx.ellipse(0, 0, TORSO.width, TORSO.height, 0, 0, TAU);
}

function torso(ctx: Ctx): void {
  torsoPath(ctx);
  ctx.fillStyle = '#e4e4e4';
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(1.5, 0, TORSO.width / 2, TORSO.height * 0.66, 0, 0, TAU);
  ctx.fillStyle = WHITE;
  ctx.fill();
  torsoPath(ctx);
  rim(ctx, 2);
}

function necklace(ctx: Ctx): void {
  line(ctx, 3, -3, 3, 3);
  ctx.lineWidth = 1;
  ctx.strokeStyle = WOOD;
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(3, 0);
  ctx.lineTo(-3.4, -1.4);
  ctx.lineTo(-3.4, 1.4);
  ctx.closePath();
  fillRim(ctx, FEATHER, 0.5);
}

function guitar(ctx: Ctx): void {
  line(ctx, 13, -3, 14.5, -19);
  ctx.lineCap = 'round';
  ctx.lineWidth = 3.8;
  ctx.strokeStyle = OUT;
  ctx.stroke();
  ctx.lineWidth = 2.2;
  ctx.strokeStyle = NECK;
  ctx.stroke();
  ctx.beginPath();
  ctx.roundRect(13, -23.5, 3.6, 5, 1);
  fillRim(ctx, HEADSTOCK, 0.8);
  circle(ctx, 5, 12.5, 1.5);
  fillRim(ctx, WOOD, 1.3);
  circle(ctx, 6.3, 12, 8.6);
  fillRim(ctx, WOOD, 1.3);
  circle(ctx, 5, 12.5, 1.5);
  ctx.fillStyle = WOOD;
  ctx.fill();
  circle(ctx, 1.8, 12.3, 4.4);
  ctx.fillStyle = HOLE;
  ctx.fill();
}

function silhouette(ctx: Ctx): void {
  ctx.fillStyle = WHITE;
  torsoPath(ctx);
  ctx.fill();
  circle(ctx, HEAD.radius, HEAD.along, 0);
  ctx.fill();
  circle(ctx, 5, 12.5, 1.5);
  ctx.fill();
  circle(ctx, 6.3, 12, 8.6);
  ctx.fill();
  line(ctx, 13, -3, 14.5, -19);
  ctx.lineCap = 'round';
  ctx.lineWidth = 3.8;
  ctx.strokeStyle = WHITE;
  ctx.stroke();
  ctx.beginPath();
  ctx.roundRect(13, -23.5, 3.6, 5, 1);
  ctx.fill();
}

function hand(ctx: Ctx): void {
  circle(ctx, 3.2);
  ctx.fillStyle = WHITE;
  ctx.fill();
  rim(ctx, 1.3);
}

function head(ctx: Ctx): void {
  circle(ctx, HEAD.radius, HEAD.along, 0);
  ctx.fillStyle = WHITE;
  ctx.fill();
  rim(ctx, 1.5);
}

function crown(ctx: Ctx): void {
  circle(ctx, HEAD.capRadius, HEAD.cap, 0);
  ctx.fillStyle = HAIR_CAP;
  ctx.fill();
  for (const side of [-1, 1]) {
    circle(ctx, 2.3, 6.8, side * 2.8);
    ctx.fillStyle = LENS;
    ctx.fill();
    ctx.lineWidth = 0.9;
    ctx.strokeStyle = RIM;
    ctx.stroke();
  }
  line(ctx, 6.8, -0.5, 6.8, 0.5);
  ctx.lineWidth = 0.9;
  ctx.strokeStyle = RIM;
  ctx.stroke();
  ctx.globalAlpha = 0.6;
  ctx.fillStyle = WHITE;
  for (const side of [-1, 1]) {
    circle(ctx, 0.6, 6.2, side * 2.8 - 0.5);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

function sunflower(ctx: Ctx): void {
  for (let index = 0; index < 12; index += 1) {
    const angle = (index * TAU) / 12;
    ctx.beginPath();
    ctx.ellipse(Math.cos(angle) * 4.2, Math.sin(angle) * 4.2, 2.4, 1.1, angle, 0, TAU);
    fillRim(ctx, PETAL, 0.5);
  }
  circle(ctx, 2.8);
  fillRim(ctx, HEART, 0.6);
}

const NOTE_SIZE = 3;
const NOTE_RADIUS = 9 * NOTE_SIZE;
function note(ctx: Ctx): void {
  ctx.scale(NOTE_SIZE, NOTE_SIZE);
  ctx.beginPath();
  ctx.ellipse(-1.6, 2.6, 2.7, 2, -0.4, 0, TAU);
  ctx.fillStyle = WHITE;
  ctx.fill();
  ctx.lineWidth = 0.6;
  ctx.strokeStyle = OUT;
  ctx.stroke();
  line(ctx, 0.9, 2, 0.9, -5.6);
  ctx.lineCap = 'round';
  ctx.lineWidth = 2.1;
  ctx.strokeStyle = OUT;
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(0.9, -5.6);
  ctx.quadraticCurveTo(4.2, -4, 3.4, -0.8);
  ctx.stroke();
  line(ctx, 0.9, 2, 0.9, -5.6);
  ctx.lineWidth = 1.3;
  ctx.strokeStyle = WHITE;
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(0.9, -5.6);
  ctx.quadraticCurveTo(4.2, -4, 3.4, -0.8);
  ctx.stroke();
}

function soft(width: number, height: number): Piece {
  return [
    96,
    96,
    REFERENCE_RADIUS,
    (ctx) => {
      glow(ctx, WHITE, 8);
      ctx.fillStyle = WHITE;
      ctx.beginPath();
      ctx.ellipse(0, 0, width, height, 0, 0, TAU);
      ctx.fill();
    },
  ];
}

const HALF_TIME_RADIUS = 120;
const DASHES = 28;
function dashes(ctx: Ctx): void {
  ctx.lineCap = 'round';
  ctx.lineWidth = 5;
  ctx.strokeStyle = WHITE;
  for (let index = 0; index < DASHES; index += 1) {
    const from = (index * TAU) / DASHES;
    ctx.beginPath();
    ctx.arc(0, 0, HALF_TIME_RADIUS - 4, from, from + (TAU / DASHES) * 0.55);
    ctx.stroke();
  }
}

function floor(ctx: Ctx): void {
  const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, HALF_TIME_RADIUS);
  gradient.addColorStop(0, 'rgb(255 255 255 / 0.25)');
  gradient.addColorStop(0.85, 'rgb(255 255 255 / 0.7)');
  gradient.addColorStop(1, 'rgb(255 255 255 / 0)');
  ctx.fillStyle = gradient;
  circle(ctx, HALF_TIME_RADIUS);
  ctx.fill();
}

export const BABACOOL_PIECES = {
  silhouette: [80, 104, REFERENCE_RADIUS, units(silhouette)],
  torso: [40, 56, REFERENCE_RADIUS, units(torso)],
  necklace: [20, 20, REFERENCE_RADIUS, units(necklace)],
  guitar: [76, 104, REFERENCE_RADIUS, units(guitar)],
  hand: [16, 16, REFERENCE_RADIUS, units(hand)],
  head: [40, 32, REFERENCE_RADIUS, units(head)],
  crown: [44, 32, REFERENCE_RADIUS, units(crown)],
  sunflower: [32, 32, REFERENCE_RADIUS, units(sunflower)],
  shadow: soft(22, 28),
} satisfies Record<string, Piece>;

export const DUB_PIECES = {
  note: [60, 60, NOTE_RADIUS, note],
  dashes: [248, 248, HALF_TIME_RADIUS, dashes],
  floor: [248, 248, HALF_TIME_RADIUS, floor],
} satisfies Record<string, Piece>;
